import React from 'react'

import { useQuery } from '@apollo/client'
import { Skeleton } from '@factorialco/deprecated-design-system'
import {
  CurrencySelectorCurrencyFragment,
  GetCurrencySelectorCurrenciesDocument,
} from 'generated/resources/graphql'

import Selector from 'modules/core/components/Selector'
import i18n from 'modules/core/lib/i18n'

import Loading from 'modules/finance/components/Loading'

interface CommonProps {
  isDisabled?: boolean
  defaultValue?: string
  onChange?: (value: string) => void
  mandatory?: boolean
  currencies?: CurrencySelectorCurrencyFragment[]
  label?: string
  secondaryLabel?: React.ReactNode | React.ReactNode[]
  error?: string
}

interface ControlledProps extends CommonProps {
  value: string
  defaultValue?: undefined
}

interface UncontrolledProps extends CommonProps {
  value?: undefined
  defaultValue: string
}

const CurrencySelector: React.FC<ControlledProps | UncontrolledProps> = ({
  currencies,
  mandatory,
  value,
  defaultValue,
  onChange,
  isDisabled,
  label,
  secondaryLabel,
  error,
}) => {
  const skip = !!currencies
  const { data, error: currencyError } = useQuery(GetCurrencySelectorCurrenciesDocument, {
    skip,
  })

  const handleChange = (val: unknown) => {
    // eslint-disable-next-line no-type-assertion/no-type-assertion
    if (!isDisabled) onChange?.(val as string)
  }

  const options = data?.apiCore.currenciesConnection.edges.map((edge) => edge.node) || currencies

  const currentValue = value ?? defaultValue
  const selectorValue = currentValue && currentValue.trim() !== '' ? [currentValue] : []

  return (
    <Loading skeleton={<Skeleton />} loading={!data && !currencyError && !skip}>
      <Selector.Popover
        mandatory={mandatory}
        value={selectorValue}
        label={label ?? i18n.t('finance.currency')}
        error={error}
        placeholder={i18n.t('common.select')}
        onChange={handleChange}
        blankLabel=""
        isDisabled={isDisabled}
        secondaryLabel={secondaryLabel}
      >
        {options?.map((option) => (
          <Selector.Option
            key={option.isoCode}
            value={option.isoCode}
            label={option.isoCode}
            searchValue={option.isoCode}
          >
            {option.isoCode}
          </Selector.Option>
        )) ?? []}
      </Selector.Popover>
    </Loading>
  )
}

export default CurrencySelector
